<template>
  <div class="component-container" v-safe-area-top
       :style="{
      padding: padding,
      backgroundImage: `url(${tarotBoxHistory.output_page_meta_data.background_image_url})`,
      backgroundColor: backgroundColor}">
    <div class="content-wrapper">
      <div class="heading-texts">
        <img id="symbol" :src="tarotBoxHistory.output_page_meta_data.objet_icon_image_url" alt="symbol"/>
        <span class="font14 gray5">
          <strong>Chapter {{ currentChapterIndex }}</strong>
        </span>
        <span class="serif gray1 font20">{{ currentPageData.page_title }}</span>
      </div>
      <div class="tarot-info" v-if="currentPageData.page_content.related_tarot_card_info.length > 0">
        <div class="tarot-border">
        </div>
        <div v-for="(card, index) in currentPageData.page_content.related_tarot_card_info"
             :key="index"
             class="tarot-card-section">
          <TarotLine v-if="index === 0" fillClass="svg-fill-gray9"/>
          <div class="tarot-card-infos">
            <ShimmerImage :src="card.card_img_url"
                          :heightRatio="136/85"
                          :borderRadius="'3px'"
                          :imageStyle="{
                                width: '23vw',
                                '-webkit-filter': 'drop-shadow(5px 5px 5px rgba(0,0,0,0.2))',
                                'filter': 'drop-shadow(5px 5px 5px rgba(0,0,0,0.2))'}"/>
            <div class="tarot-card-texts">
              <div class="bold gray2">{{card.card_name}}</div>
              <div class="tarot-card-meaning">
                <div class="bold gray4 font14">의미</div>
                <div class="font12 gray4" style="word-break: keep-all">{{card.meaning}}</div>
              </div>
              <div class="tarot-card-meaning">
                <div class="bold gray4 font14">주요 상징</div>
                <div class="font12 gray4">{{card.sign}}</div>
              </div>
            </div>
          </div>
          <TarotLine fillClass="svg-fill-gray9"/>
        </div>
      </div>
      <div class="white-space" style="height: 24px; width: 100%;" v-else></div>
      <div class="content-texts serif gray4" v-html="formattedContent"></div>
    </div>
  </div>

  <!-- 심층풀이 섹션 추가 -->
  <div v-if="extraQuestions.length > 0" class="extra-questions-section">
    <h1 class="Gray3 Title1">
      <img src="@/tarot_box/pages/box_result_v2/assets/extra-question.svg"/>
      심층풀이
    </h1>
    <div v-for="(item, index) in extraQuestions"
         :key="`extra-${index}`"
         class="extra-question-item"
         :class="{ 'collapsed': isCollapsed(index) }">
      <!-- 질문 -->
      <div class="extra-question-bubble">
        <div class="question-wrapper" @click="toggleCollapse(index)">
          <div class="question-text serif"
               :class="{ 'question-collapsed': isCollapsed(index) }">
            {{ item.question }}
          </div>
          <button class="toggle-button">
            <img :src="isCollapsed(index)
              ? require('@/tarot_box/pages/box_result_v2/assets/expand.svg')
              : require('@/tarot_box/pages/box_result_v2/assets/collapse.svg')"
                 alt="toggle"/>
          </button>
        </div>
      </div>

      <!-- 답변 -->
      <transition name="slide-fade">
        <div class="extra-answer-bubble" v-if="item.answer && !isCollapsed(index)">
          <TarotLine v-if="index === 0" fillClass="svg-fill-gray9"/>
          <div class="answer-text serif" v-html="formatExtraAnswer(item.answer)"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ShimmerImage from "@/tarot_box/components/shimmer/ShimmerImage.vue";
import TarotLine from "@/tarot_box/pages/box_result_v2/assets/detail-page/TarotLine.vue";
import { marked } from 'marked';

export default {
  name: "AnswerComponent",
  components: {ShimmerImage, TarotLine},
  props: {
    currentIndex: {
      type: Number,
    },
    currentPageData: {
      type: Object,
    },
    backgroundColor: {
      type: String,
    },
    tarotBoxHistory: {
      type: Object,
    },
    currentChapterIndex: {
      type: Number,
    },
    isShare: {
      type: Boolean,
    },
  },
  emits: ['open-card-info', 'extra-questions-visible'],
  data() {
    return {
      collapsedStates: {} // 각 아이템의 접힘 상태 관리
    };
  },
  computed: {
    extraQuestions() {
      const questions = [];

      // 1. 추가 심층풀이들 (extra_question_histories) - 최신순 정렬
      if (this.tarotBoxHistory.extra_question_histories) {
        const additionalQuestions = this.tarotBoxHistory.extra_question_histories
            .filter(item => item.page_index === this.currentIndex)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))  // 최신순 정렬 추가
            .map(item => ({
              ...item,
              type: 'additional'
            }));

        questions.push(...additionalQuestions);
      }

      // 2. 최초 심층풀이 (가장 마지막에 추가)
      if (this.tarotBoxHistory.extra_question_page_index === this.currentIndex) {
        questions.push({
          question: this.tarotBoxHistory.extra_question,
          answer: this.tarotBoxHistory.extra_answer,
          status: this.tarotBoxHistory.extra_question_status,
          type: 'initial',
          created_at: this.tarotBoxHistory.updated_at
        });
      }

      return questions;
    },
    formattedContent() {
      let content = this.currentPageData.page_content.content;
      content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      content = content.replace(/\*(.*?)\*/g, '<em>$1</em>');
      content = marked(content);

      // 변환된 HTML 파싱
      const tempElement = document.createElement('div');
      tempElement.innerHTML = content;

      // 모든 <p> 요소에 gray4 클래스 추가
      const paragraphs = tempElement.getElementsByTagName('p');
      for (let i = 0; i < paragraphs.length; i++) {
        paragraphs[i].classList.add('gray4');
      }

      // 수정된 HTML 반환
      return tempElement.innerHTML;
    },
    padding() {
      return this.isShare ? '48px 24px 200px 24px' : '100px 24px 200px 24px';
    }
  },
  mounted() {
    this.initializeCollapsedStates();

    // 1. 즉시 실행 시 상태 확인
    console.log('Mounted - extraQuestions:', this.extraQuestions.length);

    // 2. DOM이 완전히 렌더링된 후 실행
    this.$nextTick(() => {
      console.log('NextTick - DOM ready');
      const section = document.querySelector('.extra-questions-section');
      console.log('Section found in nextTick:', !!section);

      // 3. 약간의 지연 후 실행
      setTimeout(() => {
        console.log('After timeout - setting up observer');
        this.setupIntersectionObserver();
      }, 100);
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    formatExtraAnswer(answer) {
      // |END| 태그 제거
      let formattedAnswer = answer.replace(/\|END\|/g, '');

      // **bold** 처리
      formattedAnswer = formattedAnswer.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // <br><br> 처리
      formattedAnswer = formattedAnswer.replace(/<br><br>/g, '</p><p>');

      // 첫 번째와 마지막에 <p> 태그 추가
      formattedAnswer = `<p>${formattedAnswer}</p>`;

      return formattedAnswer;
    },

    initializeCollapsedStates() {
      // Vue 3에서는 직접 할당으로 반응성 유지
      const newStates = {};
      this.extraQuestions.forEach((_, index) => {
        newStates[index] = index !== 0;
      });
      this.collapsedStates = newStates;
    },

    isCollapsed(index) {
      return this.collapsedStates[index] ?? (index !== 0);
    },

    toggleCollapse(index) {
      // Vue 3에서는 직접 할당으로 반응성 유지
      this.collapsedStates = {
        ...this.collapsedStates,
        [index]: !this.isCollapsed(index)
      };
    },
    callAnswerMethod(methodName) {
      if (this[methodName] && typeof this[methodName] === 'function') {
        this[methodName]();
      }
    },

    scrollToExtraQuestion() {
      // 심층풀이 섹션으로 스크롤 이동
      const extraSection = document.querySelector('.extra-questions-section');
      if (extraSection) {
        extraSection.scrollIntoView({behavior: 'smooth'});
      }
    },
    setupIntersectionObserver() {
      // 1. 먼저 심층풀이가 있는지 확인
      console.log('Setting up observer, extraQuestions:', this.extraQuestions);
      if (!this.extraQuestions || this.extraQuestions.length === 0) {
        console.log('No extra questions, exiting');
        return;
      }

      // 2. 답변이 있는 심층풀이가 있는지 확인
      const hasAnsweredQuestions = this.extraQuestions.some(q =>
          q.answer && q.answer.trim() !== ''
      );
      console.log('Has answered questions:', hasAnsweredQuestions);

      if (!hasAnsweredQuestions) {
        console.log('No answered questions, exiting');
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.1, 0.3, 0.5, 0.8] // 여러 threshold로 테스트
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          console.log('Observer callback:', {
            isIntersecting: entry.isIntersecting,
            ratio: entry.intersectionRatio,
            threshold: entry.threshold
          });

          if (entry.isIntersecting && entry.intersectionRatio >= 0.1) { // 10% 이상 보일 때
            // 로컬 읽음 상태 체크
            const readStatus = this.$parent?.extraQuestionReadStatus || {};
            console.log('Parent read status:', readStatus);

            const hasUnreadWithAnswer = this.extraQuestions.some(q => {
              let isUnread = false;
              let key = '';

              if (q.type === 'initial') {
                key = `initial_${this.currentIndex}`;
                isUnread = !readStatus[key]?.isRead;
              } else {
                key = `extra_${q.id}`;
                isUnread = !readStatus[key]?.isRead;
              }

              console.log('Checking question:', {
                question: q.question,
                type: q.type,
                id: q.id,
                key: key,
                currentIndex: this.currentIndex,
                isUnread: isUnread,
                hasAnswer: !!q.answer,
                answerLength: q.answer ? q.answer.length : 0
              });

              return isUnread && q.answer && q.answer.trim() !== '';
            });

            console.log('hasUnreadWithAnswer result:', hasUnreadWithAnswer);

            if (hasUnreadWithAnswer) {
              console.log('✅ Emitting extra-questions-visible event');
              this.$emit('extra-questions-visible');
              this.observer.disconnect();
            } else {
              console.log('❌ No unread questions with answers, continue observing');
              // disconnect하지 않고 계속 관찰
            }
          }
        });
      }, options);

      // 심층풀이 섹션 관찰
      this.$nextTick(() => {
        const extraSection = document.querySelector('.extra-questions-section');
        console.log('Looking for extra section...');

        if (!extraSection) {
          // 컴포넌트 내부에서도 찾아보기
          const localSection = this.$el.querySelector('.extra-questions-section');
          console.log('Trying local search:', !!localSection);

          if (localSection) {
            this.observer.observe(localSection);
            console.log('✅ Started observing (local)');
          } else {
            console.log('❌ No section found');
          }
        } else {
          this.observer.observe(extraSection);
          console.log('✅ Started observing (global)');
        }
      });
    },
  },
  watch: {
    extraQuestions: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          // extraQuestions가 생성된 후에 Observer 설정
          this.$nextTick(() => {
            console.log('Extra questions changed, setting up observer');
            this.setupIntersectionObserver();
          });
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
/* 기존 스타일 유지 */
p{
  font-size: 16px !important;
  font-weight: 400 !important;
  line-height: 26px !important;
  .gray4{
    font-size: 16px !important;
  }
}

.tarot-card-section{
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.tarot-card-infos{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 30px 12px;
  box-sizing: border-box;
  gap: 20px;
}

.tarot-card-texts{
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  height: calc(23vw / 85 * 136);
  width: 60%;
  text-align: left;
  padding-right: 30px;
  box-sizing: border-box;
}

.tarot-info{
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 40px;
}

.tarot-border{
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100% / 111 * 2);
  margin: 0 auto;
  img{
    width: 100%;
  }
}

.component-container {
  width: 100vw;
  min-height: 100vh;
  background-size: 100% auto;
  background-position: bottom;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
}

.heading-texts {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 6px;
}

#symbol {
  height: 30px;
}

.content-texts {
  text-align: left;
  line-height: 26px;
  padding-top: 24px;
  box-sizing: border-box;
  margin-bottom: 24px;
}

.extra-questions-section{
  display: flex;
  flex-direction: column;
  background: var(--extra-section-bg);  /* CSS 변수로 변경 */
  padding: 43px 20px 180px;
  gap: 12px;
}

.extra-questions-section h1{
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  margin-bottom: 12px;
  color: var(--gray2);  /* 제목 색상도 테마 따라가도록 */
}

.dark-mode .extra-questions-section h1 img{
  filter: brightness(300%) hue-rotate(-20deg);

}

.extra-question-item{
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--extra-item-bg);  /* CSS 변수로 변경 */
  border-radius: 12px;
  padding: 14px;
  text-align: left;
  transition: all 0.3s ease;
}

.extra-question-item.collapsed {
  gap: 0;
}

.question-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.question-text{
  color: var(--extra-question-color);  /* CSS 변수로 변경 */
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
  flex: 1;
}

.question-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.dark-mode


.toggle-button img {
  width: 20px;
  height: 20px;
}

.answer-text :deep(p) {
  color: var(--extra-answer-color);  /* CSS 변수로 변경 */
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.28px;
  padding: 0;
  white-space: normal;
  margin-bottom: 30px;
}

.answer-text :deep(p:last-child) {
  margin-bottom: 0;
}

/* 슬라이드 애니메이션 */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.dark-mode .extra-answer-bubble svg{
  filter: brightness(130%);
}
</style>